import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { inlineButtons, InlineFundingButtons } from './InlineButtonsTypes';
import * as assetTypes from '../../components/AssetClass/assetTypes';
import InlineFundingNavigation from '../InlineFundingNavigation';
import Config from '../../Config';
import * as amplitude from '../../lib/amplitude';

const config = new Config();

// map button ids to transfer_method for amplitude
const transferMethodMap = {
  linkBankAccount: 'ach',
  onlineTransfer: 'ach',
  wireTransfer: 'wire',
  transferAccount: 'acat',
  checkDeposit: 'check',
  transferService: 'transfer_service',
  rollover401k: 'rollover',
};

const craftFundingEventProps = (props) => {
  const isTseu = props.application && props.application.isTSEU;
  const eventProperties = {
    application_id: props.applicationId,
    tradestation_entity: isTseu ? 'tseu' : 'tss',
  };
  if (props.accountType && props.assetClass) {
    eventProperties.account_type = amplitude.mapAccountType2(props.accountType);
    eventProperties.equities_account = props.accountType.assetTypes.includes('equities');
    eventProperties.futures_account = props.accountType.assetTypes.includes('futures');
    eventProperties.futures_options_account = props.assetClass.tradeFuturesOptions;
  }
  return eventProperties;
};

const sendFundingMethodsPresented = (props) => {
  const fdcnId = props.registration ? props.registration.fdcnId : null;
  const eventProperties = {
    ...craftFundingEventProps(props),
    country_of_residence: props.address && props.address[0] && props.address[0].residentialCountry,
  };
  amplitude.trackEvent('aop_funding_methods_presented', eventProperties, fdcnId);
};

const sendFundingMethodSelected = (props, transferMethod) => {
  const fdcnId = props.registration ? props.registration.fdcnId : null;
  const eventProperties = {
    ...craftFundingEventProps(props),
    transfer_method: transferMethod,
  };
  amplitude.trackEvent('aop_funding_method_selected', eventProperties, fdcnId);
};

const InlineFunding = (props) => {
  const {
    registration,
    address,
    accountType,
    onBack,
    onOptionClick,
    displayAdditionalOptions,
    showIntermediateOptions,
  } = props;
  const [isDisplayingAdditionalOptions, setIsDisplayingAdditionalOptions] = useState(displayAdditionalOptions);
  const isExistingCustomer = registration ? registration.isExistingCustomer : false;
  const isDomestic = address && address.length > 0 && address[0].residentialCountry === 'US';
  const isCrypto = accountType && accountType.assetTypes
    ? accountType.assetTypes.includes(assetTypes.ASSET_CLASS_TYPES_CRYPTO) ||
    accountType.assetTypes.includes(assetTypes.ASSET_CLASS_TYPES_CRYPTO_WAITLIST)
    : false;
  const isAnEquitiesAccount = accountType && accountType.assetTypes
    ? accountType.assetTypes.includes(assetTypes.ASSET_CLASS_TYPES_EQUITIES)
    : false;
  const isIra = accountType && accountType.accountType && accountType.accountType.includes('ira');

  const enableAdditionalOptionsBtn = () => {
    if (config.rollover401kEnabled && isAnEquitiesAccount) {
      return showIntermediateOptions && !isDisplayingAdditionalOptions && config.inlineFunding === 'V3';
    }
    return !isDisplayingAdditionalOptions && config.inlineFunding === 'V3';
  };

  const onBackHandler = () => {
    return isDisplayingAdditionalOptions ? backToDefaultOptions : onBack;
  };

  const backToDefaultOptions = () => {
    setIsDisplayingAdditionalOptions(false);
  };

  const redirectToAdditionalOptions = () => {
    setIsDisplayingAdditionalOptions(true);
  };

  useEffect(() => {
    sendFundingMethodsPresented(props); // fire amplitude event when this component mounts
  }, []);

  const handleOptionClick = (button, isDisplayingAdditional) => {
    const buttonKey = Object.keys(InlineFundingButtons).find(k => InlineFundingButtons[k].id === button.id);
    const transferMethod = buttonKey ? transferMethodMap[buttonKey] : null;
    if (transferMethod) {
      sendFundingMethodSelected(props, transferMethod);
    }
    onOptionClick(button, isDisplayingAdditional);
  };

  const getButtons = () => {
    let buttons;
    if (isDisplayingAdditionalOptions) {
      buttons = getAdditionalOptionsButtons();
    } else if (showIntermediateOptions) {
      buttons = getIntermediateOptions();
    } else {
      buttons = getButtonsForInitialOptions();
    }

    return buttons.map((button, index) => {
      return (
        <div className="inline-funds-button-container">
          <div className="col col-sm-12 col-md-12 col-lg-12">
            {InlineFundingButtons[button].isExternalRedirect ?
              <a
                className={index > 0 ?
                  `${InlineFundingButtons[button].className} inline-funds-button-second`
                  : `${InlineFundingButtons[button].className} inline-funds-button-primary`}
                href={InlineFundingButtons[button].url}
                target="_blank"
                rel="noopener noreferrer"
                role="button"
              >
                {InlineFundingButtons[button].label}
              </a> :
              <button
                type="submit"
                id={`${InlineFundingButtons[button].id}_${isDomestic ? 'us' : 'int'}`}
                onClick={() => handleOptionClick(InlineFundingButtons[button], isDisplayingAdditionalOptions)}
                className={index > 0 ?
                  `${InlineFundingButtons[button].className} inline-funds-button-second`
                  : `${InlineFundingButtons[button].className} inline-funds-button-primary`}
              >
                {InlineFundingButtons[button].label}
              </button>
            }
          </div>
          {
            (!isDomestic && InlineFundingButtons[button].id === 'link_bank_btn') &&
            <div className="col col-sm-12 col-md-12 col-lg-12">
              <h5 className="button-botton-text">
                Available for bank accounts held in the U.S only
              </h5>
            </div>
          }
        </div>
      );
    });
  };

  const getButtonsForInitialOptions = () => {
    let buttons;
    const isInternational = !isDomestic;
    switch (true) {
      case isNewDomesticIraEquitiesCustomer():
        buttons = inlineButtons.newDomesticIraEquitiesCustomerOptions;
        break;
      case isExistingDomesticIraEquitiesCustomer():
        buttons = inlineButtons.existingDomesticIraEquitiesCustomerOptions;
        break;
      case isNewDomesticNonIraEquitiesCustomer():
        buttons = inlineButtons.newDomesticNonIraEquitiesCustomerOptions;
        break;
      case isExistingDomesticNonIraEquitiesCustomer():
        buttons = inlineButtons.existingDomesticNonIraEquitiesCustomerOptions;
        break;
      case isDomestic && isExistingCustomer:
        buttons = inlineButtons.existingDomesticCustomerOptions;
        break;
      case isDomestic && !isExistingCustomer:
        buttons = inlineButtons.newDomesticCustomerOptions;
        break;
      case isInternational && isExistingCustomer:
        buttons = inlineButtons.existingInternationalCustomerOptions;
        break;
      case isInternational && !isExistingCustomer:
        buttons = inlineButtons.newInternationalCustomerOptions;
        break;
      default:
        buttons = [];
        break;
    }
    if (config.inlineFunding === 'V3' && isCrypto && !buttons.includes(inlineButtons.depositCryptoOptions)) {
      const start = buttons.length - 1;
      if (isInternational && isExistingCustomer) {
        buttons.splice(start, 0, inlineButtons.depositCryptoOptions);
      }
      if (isInternational && !isExistingCustomer) {
        buttons.splice(start, 0, inlineButtons.depositCryptoOptions);
      } else if (!buttons.includes(inlineButtons.depositCryptoOptions)) {
        buttons.splice(buttons.length, 0, inlineButtons.depositCryptoOptions);
      }
    }
    if (isDomestic && isAnEquitiesAccount && !buttons.includes(inlineButtons.equitiesOnlyOptions[0])) {
      buttons.splice(buttons.length - 1, 0, inlineButtons.equitiesOnlyOptions[0]);
    }
    return buttons;
  };

  const getAdditionalOptionsButtons = () => (isDomestic ?
    inlineButtons.domesticAdditionalOptions : inlineButtons.internationalAdditionalOptions);

  const getIntermediateOptions = () => {
    if (isNewDomesticIraEquitiesCustomer()) {
      return inlineButtons.newDomesticIraEquitiesIntermediateOptions;
    }
    if (isNewDomesticNonIraEquitiesCustomer()) {
      return inlineButtons.newDomesticNonIraEquitiesIntermediateOptions;
    }
    if (isExistingDomesticIraEquitiesCustomer()) {
      return inlineButtons.existingDomesticIraEquitiesIntermediateOptions;
    }
    if (isExistingDomesticNonIraEquitiesCustomer()) {
      return inlineButtons.existingDomesticNonIraEquitiesIntermediateOptions;
    }
    return [];
  };

  const craftBackButtonName = () => {
    return isDisplayingAdditionalOptions ? 'additional-funding-options-back-btn' : 'inline-funding-back-btn';
  };

  const isDomesticEquitiesCustomer = () => isDomestic && isAnEquitiesAccount && config.rollover401kEnabled;
  const isNewDomesticIraEquitiesCustomer = () => !isExistingCustomer && isIra && isDomesticEquitiesCustomer();
  const isExistingDomesticIraEquitiesCustomer = () => isExistingCustomer && isIra && isDomesticEquitiesCustomer();
  const isNewDomesticNonIraEquitiesCustomer = () => !isExistingCustomer && !isIra && isDomesticEquitiesCustomer();
  const isExistingDomesticNonIraEquitiesCustomer = () => isExistingCustomer && !isIra && isDomesticEquitiesCustomer();

  return (
    <div className="container">
      <div className="row">
        <div className="col col-md-12 col-lg-8 col-lg-offset-2">
          <h1 className="text-center inline-funds-title">
            Let&apos;s Fund Your Account
          </h1>
        </div>
      </div>
      <div className="row">
        <div className="col col-md-12 col-lg-8 col-lg-offset-2 text-center">
          <h1 className="control-label inline-funds-subtitle">
            How would you like to fund your account?
          </h1>
        </div>
      </div>
      <div className="row">
        <div className="col col-md-12 col-lg-8 col-lg-offset-2 text-center">
          {
            getButtons()
          }
          <br />
          {enableAdditionalOptionsBtn() &&
            <div className="inline-funds-button-container">
              <btn
                id="additional-options-btn"
                className="btn btn-info slimh_element fitv_element slimv_element reset_capitals additional-options"
                onClick={redirectToAdditionalOptions}
                target="_blank"
                rel="noopener noreferrer"
                role="button"
              >
                Additional Options<i className="material-icons">arrow_right</i>
              </btn>
            </div>
          }
        </div>

      </div>
      <div className="row navigation-container">
        <InlineFundingNavigation backButtonId={craftBackButtonName()} onBack={onBackHandler()} />
      </div>
    </div>
  );
};

InlineFunding.propTypes = {
  registration: PropTypes.shape(),
  address: PropTypes.shape(),
  onBack: PropTypes.func.isRequired,
  onOptionClick: PropTypes.func.isRequired,
  accountType: PropTypes.shape(),
  displayAdditionalOptions: PropTypes.bool.isRequired,
  showIntermediateOptions: PropTypes.bool.isRequired,
};

export default InlineFunding;
